import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Head, router } from "@inertiajs/react";
import AppLayout from "@/components/AppLayout";
import {
  plan_bottom_up_by_month_edit_path,
  plan_bottom_up_by_month_export_path,
  plan_bottom_up_by_month_import_path,
  plan_bottom_up_by_month_import_revenue_path,
  plan_bottom_up_by_month_index_path,
  plan_bottom_up_by_month_submit_path,
} from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";
import ProductSearchInput from "./components/ProductSearchInput";
import { matchesProductSearch } from "./utils/productSearch";
import BottomUpGrid from "./components/BottomUpGrid";
import type { WholesaleForecastData } from "./components/ForecastAccountsCell";
import type { ScopeNode } from "@/components/ChannelMenu";
import ByMonthChannelTabs from "./components/ByMonthChannelTabs";
import type { PlanSwitcherData } from "../TopDown/components/PlanSwitcher";
import { monthRangeLabel } from "../TopDown/components/periodLabel";
import PlanHeader from "../components/PlanHeader";
import PlanNavRow from "../components/PlanNavRow";
import ByMonthToolbarActions from "./components/ByMonthToolbarActions";
import ByMonthImportDialogs from "./components/ByMonthImportDialogs";
import PricingNotice from "./components/PricingNotice";
import StickyBar from "./components/StickyBar";
import ChannelTaskWidget from "../components/ChannelTaskWidget";
import ChannelRevisionSubmitWidget from "../components/ChannelRevisionSubmitWidget";
import { RevisionBanner, DraftActivityBanner } from "../TopDown/components/EditorHeader";
import type { TaskWidget } from "../TopDown/components/PlanTaskWidget";
import { missingSummary } from "./components/monthlyMissing";
import { FlashAlert } from "@/components/Flash/FlashAlert";
import { useSaveStatus, type CellRef } from "./utils/useSaveStatus";
import { useByMonthCellWrites } from "./utils/useByMonthCellWrites";
import type { Row, AvailablePlanNode, Channel, MonthlyRevisionSubmit } from "./byMonthTypes";

interface Props {
  plan: {
    id: string;
    name: string;
    slug: string;
    begin_date: string;
    end_date: string;
    currency: string | null;
    revenue_target: number;
    org_node: { id: string; name: string };
    plan_period: { start: string; end: string }; // revision's remaining-months window; narrower than begin/end only for a revision
    actuals_months: number | null; // months of actual sales at the plan's start; null for a plain version
  };
  version: { id: string; version_number: number };
  locked_before: string; // first still-editable ISO month (later of current month and the version's plan period start)
  months: string[];
  rows: Row[];
  available_plan_nodes: AvailablePlanNode[];
  scope_tree: ScopeNode[];
  current_channel: Channel;
  can: {
    edit: boolean;
    export: boolean;
    import: boolean;
    view_gross_margin: boolean;
  };
  plan_switcher: PlanSwitcherData;
  // Not sent since 2026-09-14: wholesale channels plan in Forecast by Account and are off this page.
  wholesale_forecast?: WholesaleForecastData | null;
  // The open monthly task for this plan + channel; null unless the viewer is
  // one of its PICs.
  task_widget: TaskWidget | null;
  // Published plan, no monthly task open for the channel: the submit page
  // picks the reviewers itself (null otherwise).
  monthly_revision_submit: MonthlyRevisionSubmit | null;
  // Whether this channel has an open By Month draft (edits not yet submitted).
  draft_open: boolean;
  // Someone else's unsubmitted edits in this channel's shared draft (server
  // omits it when the viewer made the last edit).
  draft_activity: { by: string; at: string } | null;
  // First plan month that is today or later — the month the completion check
  // requires a quantity for.
  anchor_month: string | null;
}

// The editable counterpart to ByMonth.tsx, in the same page frame: every
// cell write lands on the channel's By Month draft; the header widget
// submits it.
export default function ByMonthEdit({
  plan,
  locked_before,
  months,
  rows,
  available_plan_nodes,
  scope_tree,
  current_channel,
  can,
  plan_switcher,
  wholesale_forecast,
  task_widget,
  monthly_revision_submit,
  draft_open,
  draft_activity,
  anchor_month,
}: Props) {
  const market = useMarket();
  const targetPeriodLabel = plan.actuals_months ? monthRangeLabel(plan.plan_period.start, plan.plan_period.end) : null;
  const [localRows, setLocalRows] = useState<Row[]>(rows);
  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);
  const [searchQuery, setSearchQuery] = useState("");
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [importRevenueOpen, setImportRevenueOpen] = useState(false);
  const { statusFor, markSaved, markError, dismissError, errorMessage } = useSaveStatus();

  // `draft_open` is a server prop: the first edit on a channel without a
  // draft opens one, but the page would keep showing "No changes yet" (and
  // no revision banner) until a full reload. After a successful save while
  // the prop says no draft, re-fetch just that prop.
  const draftOpenRef = useRef(draft_open);
  useEffect(() => {
    draftOpenRef.current = draft_open;
  }, [draft_open]);
  const refreshDraftOpen = useCallback(() => {
    if (!draftOpenRef.current) router.reload({ only: ["draft_open"] });
  }, []);
  const onCellsSaved = useCallback(
    (cells: CellRef[]) => {
      markSaved(cells);
      refreshDraftOpen();
    },
    [markSaved, refreshDraftOpen],
  );

  const { handleCellChange, handlePasteCells } = useByMonthCellWrites({
    marketSlug: market.slug,
    planSlug: plan.slug,
    channelId: current_channel.id,
    setLocalRows,
    markSaved: onCellsSaved,
    markError,
  });

  const missing = missingSummary(localRows, anchor_month);
  const submitHref = plan_bottom_up_by_month_submit_path(market.slug, plan.slug, current_channel.slug);
  const exportHref = plan_bottom_up_by_month_export_path(market.slug, plan.slug, current_channel.slug, { draft: 1 });
  const canEdit = can.edit;

  const visibleRows = useMemo(
    () => localRows.filter((r) => matchesProductSearch(r, searchQuery)),
    [localRows, searchQuery],
  );

  const hrefForChannel = useCallback(
    (slug: string) => plan_bottom_up_by_month_edit_path(market.slug, plan.slug, slug),
    [market.slug, plan.slug],
  );

  return (
    <AppLayout
      flush
      header={
        <PlanHeader
          name={plan.name}
          orgNodeName={plan.org_node.name}
          revenueTarget={plan.revenue_target}
          currency={plan.currency}
          beginDate={plan.begin_date}
          endDate={plan.end_date}
        />
      }
      headerActions={
        task_widget ? (
          <ChannelTaskWidget task={task_widget} missing={missing} submitHref={submitHref} />
        ) : monthly_revision_submit ? (
          <ChannelRevisionSubmitWidget
            subject="monthly plan"
            channelName={current_channel.name}
            draftOpen={draft_open}
            missing={missing}
            submitHref={submitHref}
          />
        ) : undefined
      }
    >
      <Head title={`Bottom-Up — ${plan.name} (editing)`} />
      <div>
        {/* One row, like the Products & Pricings edit page: plan switcher (no
            page tabs), the channel notice. A plan switch lands on the
            show page of the newly selected plan. */}
        <PlanNavRow
          planSwitcher={plan_switcher}
          redirectPath={plan_bottom_up_by_month_index_path(market.slug)}
          tabs={false}
          className="mx-6 mt-5 mb-4"
          center={
            <PricingNotice>
              You are editing the monthly plan for <span className="font-medium">{current_channel.name}</span>.
            </PricingNotice>
          }
        />
        {(draft_open || draft_activity) && (
          <div className="mx-6">
            {draft_open && <RevisionBanner />}
            {draft_activity && <DraftActivityBanner activity={draft_activity} />}
          </div>
        )}
        <div
          style={{ background: "var(--background)", borderColor: "var(--border)" }}
          className="border-y"
        >
          <StickyBar onHeightChange={setToolbarHeight} className="px-0 pt-0">
            <ByMonthChannelTabs
              scopeTree={scope_tree}
              currentNodeId={current_channel.id}
              hrefForChannel={hrefForChannel}
              trailing={
                <>
                  <ProductSearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    className="h-full"
                    inputClassName="rounded-none border-y-0 focus:ring-0"
                  />
                  <ByMonthToolbarActions
                    canExport={can.export}
                    canImport={can.import && canEdit}
                    exportHref={exportHref}
                    onOpenImport={() => setImportOpen(true)}
                    onOpenImportRevenue={() => setImportRevenueOpen(true)}
                    buttonClassName="-ml-px rounded-none border-y-0"
                  />
                </>
              }
            />
          </StickyBar>

          <BottomUpGrid
            stickyOffset={toolbarHeight}
            months={months}
            rows={visibleRows}
            planCurrency={plan.currency}
            planNodes={available_plan_nodes}
            editable={canEdit}
            canViewGrossMargin={can.view_gross_margin}
            lockedBefore={locked_before}
            targetPeriodLabel={targetPeriodLabel}
            onCellChange={handleCellChange}
            onPasteCells={handlePasteCells}
            saveStatusFor={statusFor}
            wholesaleForecast={wholesale_forecast}
            monthDividers
          />
        </div>
      </div>

      <ByMonthImportDialogs
        importOpen={importOpen}
        onCloseImport={() => setImportOpen(false)}
        importPath={plan_bottom_up_by_month_import_path(market.slug, plan.slug, current_channel.slug)}
        importRevenueOpen={importRevenueOpen}
        onCloseImportRevenue={() => setImportRevenueOpen(false)}
        importRevenuePath={plan_bottom_up_by_month_import_revenue_path(market.slug, plan.slug, current_channel.slug)}
        channelName={current_channel.name}
        writableMonths={months.filter((m) => m >= locked_before)}
        fileStem={`bottom-up-${plan.slug}-${current_channel.slug}`}
      />

      {errorMessage && (
        <FlashAlert message={errorMessage} onDismiss={dismissError} />
      )}
    </AppLayout>
  );
}
